import { useState, useEffect, useRef } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d6tg5ekj8d20juc903eg';

const interests = ['Living Room Furniture', 'Bedroom Furniture', 'Dining Room', 'Home Office', 'Outdoor Furniture', 'Full Room Design', 'Custom Order'];

export default function InquiryForm() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) ref.current?.classList.add('visible'); }, { threshold: 0.1 });
    if (ref.current) o.observe(ref.current);
    return () => o.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const data = new FormData(e.currentTarget);
    const params = new URLSearchParams();
    data.forEach((v, k) => params.append(k, v.toString()));
    try {
      const res = await fetch(FORM_URL, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: params.toString() });
      if (res.ok) setSubmitted(true);
      else setError('Something went wrong. Please call us or visit our showroom.');
    } catch {
      setError('Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-28 bg-cream-DEFAULT" id="inquiry">
      <div ref={ref} className="fade-up max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          {/* Left */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-gold" />
              <span className="text-gold text-xs font-semibold uppercase tracking-[0.25em]">Let's Connect</span>
            </div>
            <h2 className="font-serif text-5xl font-bold text-dark leading-tight mb-6">
              Request a Free<br />
              <em className="text-gold not-italic">Design Consultation</em>
            </h2>
            <p className="text-dark/50 text-sm leading-relaxed mb-8">
              Tell us about your space and style goals. One of our design consultants will 
              reach out within 24 hours to schedule your complimentary session — in showroom or virtual.
            </p>
            <div className="space-y-5 mb-10">
              {[
                { icon: 'ri-map-pin-line', label: 'Showroom', val: '3750 S Las Vegas Blvd, Las Vegas, NV' },
                { icon: 'ri-phone-line', label: 'Phone', val: '(702) 850-9900' },
                { icon: 'ri-time-line', label: 'Hours', val: 'Mon–Fri 10am–7pm · Sat 10am–6pm · Sun 11am–5pm' },
              ].map((c) => (
                <div key={c.label} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gold/10 flex-shrink-0">
                    <i className={`${c.icon} text-gold`} />
                  </div>
                  <div>
                    <span className="block text-[10px] text-dark/40 font-semibold uppercase tracking-wider">{c.label}</span>
                    <span className="text-dark text-sm font-medium">{c.val}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="relative overflow-hidden rounded-xl h-48">
              <img
                src="https://public.readdy.ai/ai/img_res/352296b78938063fbee62e49aef6b2c2.jpg"
                alt="Furniture design consultation Eclectic Home Las Vegas"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-dark/50 to-transparent flex items-center p-6">
                <div>
                  <p className="text-white font-serif text-lg font-bold">Visit Us In-Store</p>
                  <p className="text-white/70 text-xs mt-1">Las Vegas Showroom</p>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="bg-dark rounded-2xl p-10">
            {submitted ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gold/20 mx-auto mb-5">
                  <i className="ri-check-line text-gold text-3xl" />
                </div>
                <h3 className="font-serif text-2xl font-bold text-white mb-3">Message Received!</h3>
                <p className="text-white/50 text-sm">
                  Thank you for reaching out. Our design team will contact you within 24 hours to schedule your free consultation.
                </p>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <h3 className="font-serif text-xl font-bold text-white mb-1">Start Your Journey</h3>
                  <p className="text-white/40 text-xs">We respond within 24 hours</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-semibold text-white/40 uppercase tracking-wider mb-2">First Name *</label>
                    <input name="firstName" type="text" required placeholder="Alexandra"
                      className="w-full bg-white/8 border border-white/15 rounded-lg px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-gold transition-colors" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-white/40 uppercase tracking-wider mb-2">Last Name *</label>
                    <input name="lastName" type="text" required placeholder="Whitfield"
                      className="w-full bg-white/8 border border-white/15 rounded-lg px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-gold transition-colors" />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-semibold text-white/40 uppercase tracking-wider mb-2">Email Address *</label>
                  <input name="email" type="email" required placeholder="hello@example.com"
                    className="w-full bg-white/8 border border-white/15 rounded-lg px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-gold transition-colors" />
                </div>

                <div>
                  <label className="block text-[10px] font-semibold text-white/40 uppercase tracking-wider mb-2">Phone</label>
                  <input name="phone" type="tel" placeholder="(702) 555-0000"
                    className="w-full bg-white/8 border border-white/15 rounded-lg px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-gold transition-colors" />
                </div>

                <div>
                  <label className="block text-[10px] font-semibold text-white/40 uppercase tracking-wider mb-2">I'm Interested In *</label>
                  <select name="interest" required className="w-full bg-white/8 border border-white/15 rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:border-gold transition-colors cursor-pointer">
                    <option value="" className="bg-dark">Select an area...</option>
                    {interests.map((i) => <option key={i} value={i} className="bg-dark">{i}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-[10px] font-semibold text-white/40 uppercase tracking-wider mb-2">Tell Us About Your Space</label>
                  <textarea name="message" rows={3} maxLength={500}
                    placeholder="Room dimensions, style preferences, budget range..."
                    className="w-full bg-white/8 border border-white/15 rounded-lg px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-gold transition-colors resize-none" />
                </div>

                {error && <p className="text-red-400 text-xs">{error}</p>}

                <button type="submit" disabled={loading}
                  className="w-full bg-gold text-white py-4 rounded-lg font-bold text-sm uppercase tracking-widest hover:bg-gold-dark transition-colors cursor-pointer disabled:opacity-70 whitespace-nowrap">
                  {loading ? 'Sending...' : 'Request Free Consultation →'}
                </button>
                <p className="text-white/25 text-[11px] text-center">No obligation · Responds in 24 hours</p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
